import { Icon } from '@iconify/react'
import { Checkbox } from 'antd'
import { useNotification } from 'app/providers'
import classNames from 'classnames'
import { Select } from 'common/components/Select/Select'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { useAppSelector } from 'common/hooks/redux'
import type { IPatient } from 'features/Patients/interfaces/IPatient'
import type { Patient } from 'features/Procedure/state/procedure-api.type'
import {
  useFetchESignFormQuery,
  useFetchIntakeFormsQuery,
  useFetchPhysicianIntroductionsQuery,
  useSubmitESignFormMutation,
} from 'features/Procedure/state/procedure.api'
import { PatientSearchModal } from 'features/Schedule/pages/components/ManageAppointment/PatientSearchModal/PatientSearchModal'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { AddChartAppointment } from '../add-chart-appointment'
import { hasESignItems } from '../e-sign-button/e-sign-status.utils'
import { ESignLogModal } from '../e-sign-log-modal'
import {
  ENCOUNTER_ESIGN_COPY,
  resolveESignErrorMessage,
  resolveESignPinLength,
} from '../e-sign-modal/e-sign-modal.utils'
import { ESignModal } from '../e-sign-modal/ESignModal'
import { PatientInfoDialog } from '../patient-info-dialog'
import styles from './encounter.module.scss'
import {
  parsePatientIdFromOptionCode,
  resolvePatientOptionESignChip,
  resolveSelectedPatientOptionCode,
  type EncounterSelectOption,
  type PatientSelectOption,
} from './patient-encounter-select.utils'

type Props = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  patientOptions: PatientSelectOption[]
  encounterOptions: EncounterSelectOption[]
  isLoading: boolean
  isPatientSearchModalOpen: boolean
  patientInfo: Patient | null
  isESignStatusPending?: boolean
  isEncounterFlashing?: boolean
  newEncounterUids?: Set<string>
  onPatientChange: (patientOptionCode: string) => void
  onEncounterChange: (encounterUid: string) => void
  onSelectPatientFromModal: (patient: IPatient) => void
  onOpenPatientSearchModal: () => void
  onClosePatientSearchModal: () => void
  onEncounterDropdownOpen?: () => void
  hideCancellations?: boolean
  onHideCancellationsChange?: (hideCancellations: boolean) => void
}

export const PatientEncounterSelect = React.memo(
  ({
    selectedPatientId,
    selectedEncounterUid,
    patientOptions,
    encounterOptions,
    isLoading,
    isPatientSearchModalOpen,
    patientInfo,
    isESignStatusPending = false,
    isEncounterFlashing = false,
    newEncounterUids,
    onPatientChange,
    onEncounterChange,
    onSelectPatientFromModal,
    onOpenPatientSearchModal,
    onClosePatientSearchModal,
    onEncounterDropdownOpen,
    hideCancellations = false,
    onHideCancellationsChange,
  }: Props) => {
    const patientPopupHostRef = useRef<HTMLDivElement | null>(null)
    const encounterPopupHostRef = useRef<HTMLDivElement | null>(null)
    const [isPatientInfoDialogOpen, setIsPatientInfoDialogOpen] = useState(false)
    const [pendingAutoOpenPatientId, setPendingAutoOpenPatientId] = useState<number | null>(null)
    const hasAutoOpenedInitialPatientRef = useRef(false)
    const [isESignModalVisible, setIsESignModalVisible] = useState(false)
    const [isESignLogModalVisible, setIsESignLogModalVisible] = useState(false)
    const { setNotification } = useNotification()
    const { siteId } = useParams<{ siteId: string }>()
    const isIntakeBlocking = useAppSelector((state) => state.procedureReducer.isIntakeBlocking)

    useFetchPhysicianIntroductionsQuery(
      { siteId: siteId!, patientId: selectedPatientId! },
      { skip: !siteId || !selectedPatientId },
    )

    useFetchIntakeFormsQuery(
      { siteId: siteId!, patientId: selectedPatientId! },
      { skip: !siteId || !selectedPatientId },
    )

    const { data: eSignStatus } = useFetchESignFormQuery(
      { siteId: siteId!, patientId: selectedPatientId!, encounterUid: selectedEncounterUid! },
      { skip: !siteId || !selectedPatientId || !selectedEncounterUid },
    )

    const [submitESignForm, { isLoading: isSubmitting }] = useSubmitESignFormMutation()

    const getPatientPopupContainer = useCallback(() => {
      return patientPopupHostRef.current ?? document.body
    }, [])

    const getEncounterPopupContainer = useCallback(() => {
      return encounterPopupHostRef.current ?? document.body
    }, [])

    const encounterSignedData = useMemo(() => {
      if (!eSignStatus?.encounter) return null
      const status = eSignStatus.encounter
      return status.is_signed === 1 && status.items && status.items.length > 0 ? status : null
    }, [eSignStatus])

    const isEncounterSigned = encounterSignedData !== null
    const hasEncounterSignHistory = hasESignItems(eSignStatus?.encounter)
    const hasEncounterFormToSign = eSignStatus?.encounter?.has_form_to_sign === 1
    const shouldShowESignButton =
      Boolean(selectedEncounterUid) && (hasEncounterFormToSign || isESignStatusPending)

    const selectedPatientOptionCode = useMemo(
      () => resolveSelectedPatientOptionCode(patientOptions, selectedPatientId, selectedEncounterUid),
      [patientOptions, selectedPatientId, selectedEncounterUid],
    )

    const renderPatientOption = (option: PatientSelectOption) => {
      const isSelectedPatient = option.code === selectedPatientOptionCode
      const eSignChip = resolvePatientOptionESignChip(
        option,
        isSelectedPatient,
        selectedEncounterUid,
      )

      return (
        <div className={styles.patientOptionContent}>
          <span className={styles.patientOptionLine}>
            {option.staffInitials && (
              <span className={styles.patientInitialsAvatar}>{option.staffInitials}</span>
            )}
            {option.appointmentTimeLabel && (
              <span className={styles.patientOptionTime}>{option.appointmentTimeLabel}</span>
            )}
            <span className={styles.patientNameWithUnderline}>
              <span className={styles.patientOptionName}>{option.patientName}</span>
              {option.statusColor && (
                <span
                  className={styles.patientOptionUnderline}
                  style={{ backgroundColor: option.statusColor }}
                />
              )}
            </span>
            {eSignChip && (
              <span className={styles.eSignChip} title='Signed forms'>
                {eSignChip}
              </span>
            )}
          </span>
        </div>
      )
    }

    const handlePatientOptionRender = (option: any) => {
      return renderPatientOption(option.data as PatientSelectOption)
    }

    const renderEncounterOption = (option: EncounterSelectOption) => {
      return (
        <div className={styles.encounterOptionContent}>
          <span className={styles.encounterOptionLine}>
            {option.providerInitials && (
              <span
                className={styles.encounterInitialsAvatar}
                title={option.providerName || option.providerInitials}>
                {option.providerInitials}
              </span>
            )}
            <span className={styles.encounterOptionDate}>{option.dateLabel}</span>
            {option.encounterTitle && (
              <span className={styles.encounterOptionTitle}>{option.encounterTitle}</span>
            )}
          </span>
        </div>
      )
    }

    const handleEncounterOptionRender = (option: any) => {
      return renderEncounterOption(option.data as EncounterSelectOption)
    }

    const patientSelectOptions = patientOptions.map((option) => ({
      ...option,
      label: renderPatientOption(option),
      title: option.searchText,
    }))

    const handlePatientChange = useCallback(
      (patientOptionCode: string) => {
        setPendingAutoOpenPatientId(parsePatientIdFromOptionCode(patientOptionCode))
        onPatientChange(patientOptionCode)
      },
      [onPatientChange],
    )

    useEffect(() => {
      if (pendingAutoOpenPatientId === null) return
      if (patientInfo?.patient_id !== pendingAutoOpenPatientId) return
      setIsPatientInfoDialogOpen(true)
      setPendingAutoOpenPatientId(null)
    }, [pendingAutoOpenPatientId, patientInfo])

    useEffect(() => {
      if (hasAutoOpenedInitialPatientRef.current) return
      if (!patientInfo?.patient_id) return
      hasAutoOpenedInitialPatientRef.current = true
      setIsPatientInfoDialogOpen(true)
    }, [patientInfo])

    const handleOpenPatientInfoDialog = () => {
      setIsPatientInfoDialogOpen(true)
    }

    const handleClosePatientInfoDialog = () => {
      setIsPatientInfoDialogOpen(false)
    }

    const handleOpenESignModal = () => {
      if (isEncounterSigned) {
        setIsESignLogModalVisible(true)
        return
      }
      setIsESignModalVisible(true)
    }

    const handleCloseESignModal = () => {
      setIsESignModalVisible(false)
    }

    const renderPatientDropdown = useCallback(
      (menu: React.ReactElement) => (
        <>
          <div
            className={styles.hideCancellationsRow}
            onMouseDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            onClick={() => onHideCancellationsChange?.(!hideCancellations)}>
            <Checkbox checked={hideCancellations} />
            <span className={styles.hideCancellationsLabel}>Hide Cancellations</span>
          </div>
          {menu}
        </>
      ),
      [hideCancellations, onHideCancellationsChange],
    )

    const handleEncounterDropdownOpenChange = useCallback(
      (open: boolean) => {
        if (open) {
          onEncounterDropdownOpen?.()
        }
      },
      [onEncounterDropdownOpen],
    )

    const encounterOptionsWithHighlight = useMemo(() => {
      if (!newEncounterUids || newEncounterUids.size === 0) return encounterOptions
      return encounterOptions.map((option) => ({
        ...option,
        className: newEncounterUids.has(option.code as string)
          ? styles.newEncounterHighlight
          : undefined,
      }))
    }, [encounterOptions, newEncounterUids])

    const encounterSelectOptions = encounterOptionsWithHighlight.map((option) => ({
      ...option,
      label: renderEncounterOption(option),
      title: option.searchText,
    }))

    const handleESignSubmit = async (pin: string): Promise<boolean> => {
      if (!selectedPatientId || !selectedEncounterUid) return false

      try {
        const result = await submitESignForm({
          siteId: siteId!,
          patientId: selectedPatientId,
          encounterUid: selectedEncounterUid,
          payload: {
            form_name: 'encounter',
            password: pin,
            amendment: '',
          },
        }).unwrap()

        if (result.success) {
          setIsESignModalVisible(false)
          return true
        } else {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: result.message || 'Failed to sign encounter',
          })
          return false
        }
      } catch (error: unknown) {
        const errorMessage = resolveESignErrorMessage(error, 'Failed to sign encounter')
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: errorMessage,
        })
        return false
      }
    }

    return (
      <>
        <PatientSearchModal
          isOpen={isPatientSearchModalOpen}
          onClose={onClosePatientSearchModal}
          onSelectPatient={onSelectPatientFromModal}
        />
        <PatientInfoDialog
          isOpen={isPatientInfoDialogOpen}
          onClose={handleClosePatientInfoDialog}
          patientInfo={patientInfo}
        />
        <div className={styles.encounterSelectContainer}>
          <AddChartAppointment disabled={!selectedEncounterUid} />
          <div className={styles.patientSelectContainer} ref={patientPopupHostRef}>
            <button
              className={styles.iconButtonPrefix}
              onClick={handleOpenPatientInfoDialog}
              disabled={isLoading || !selectedPatientId || !patientInfo}>
              <Icon icon='lucide:contact-round' width={20} height={20} />
            </button>
            <Select
              containerClassName={styles.patientSelectContainer}
              className={styles.patientSelect}
              value={selectedPatientOptionCode}
              onChange={handlePatientChange}
              listOptions={patientSelectOptions}
              optionRender={handlePatientOptionRender}
              optionLabelProp='label'
              virtual={false}
              placeholder='Select patient'
              disabled={isLoading || isIntakeBlocking}
              getPopupContainer={getPatientPopupContainer}
              popupRender={renderPatientDropdown}
              classNames={{
                popup: {
                  root: styles.touchContainedDropdown,
                },
              }}
            />
            <button
              className={styles.iconButton}
              onClick={onOpenPatientSearchModal}
              disabled={isLoading}>
              <Icon icon='mdi:magnify' width={20} height={20} />
            </button>
          </div>

          <div
            className={classNames(styles.encounterSelectWrapper, {
              [styles.encounterFlashing]: isEncounterFlashing,
            })}
            ref={encounterPopupHostRef}>
            <Select
              containerClassName={styles.selectContainer}
              className={classNames(styles.encounterSelect, {
                [styles.encounterSelectWithSuffix]: shouldShowESignButton,
              })}
              value={selectedEncounterUid || undefined}
              onChange={onEncounterChange}
              listOptions={encounterSelectOptions}
              optionRender={handleEncounterOptionRender}
              optionLabelProp='label'
              placeholder='Select encounter'
              disabled={isLoading || !selectedPatientId || isIntakeBlocking}
              getPopupContainer={getEncounterPopupContainer}
              onDropdownVisibleChange={handleEncounterDropdownOpenChange}
              classNames={{
                popup: {
                  root: styles.touchContainedDropdown,
                },
              }}
            />
            {shouldShowESignButton && (
              <button
                type='button'
                className={classNames(styles.eSignButton, {
                  [styles.eSignButtonHistory]: hasEncounterSignHistory,
                })}
                onClick={handleOpenESignModal}
                disabled={isLoading || isESignStatusPending}>
                <span>eSign</span>
              </button>
            )}
          </div>
        </div>
        <ESignModal
          isVisible={isESignModalVisible}
          onClose={handleCloseESignModal}
          onSubmit={handleESignSubmit}
          description={ENCOUNTER_ESIGN_COPY}
          pinLength={resolveESignPinLength(eSignStatus)}
          signHistory={eSignStatus?.encounter?.items}
          isLoading={isSubmitting}
        />
        <ESignLogModal
          isVisible={isESignLogModalVisible}
          onClose={() => setIsESignLogModalVisible(false)}
          signedData={encounterSignedData}
        />
      </>
    )
  },
)
